import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, Target, Lightbulb, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-fade-in-up');
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.scroll-animate').forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const values = [
    {
      icon: 'Lightbulb',
      title: 'Innovation',
      description: 'We constantly push boundaries and explore new technologies to deliver cutting-edge solutions.'
    },
    {
      icon: 'Target',
      title: 'Excellence',
      description: 'We are committed to delivering exceptional quality in every project we undertake.'
    },
    {
      icon: 'Users',
      title: 'Collaboration',
      description: 'We work closely with our clients, understanding their vision and bringing it to life together.'
    },
    {
      icon: 'Award',
      title: 'Integrity',
      description: 'We believe in transparent communication and ethical business practices with all our stakeholders.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative min-h-[60vh] flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 overflow-hidden pt-20">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div
          className="absolute top-20 right-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"
          style={{ transform: `translateY(${scrollY * 0.3}px)` }}
        ></div>
        <div
          className="absolute bottom-20 left-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"
          style={{ transform: `translateY(${-scrollY * 0.2}px)` }}
        ></div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 py-24 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-light tracking-tight text-white mb-6">
            About <span className="font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">ByteBios</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            Transforming businesses through innovative technology solutions since 2016
          </p>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="py-24 bg-gradient-to-b from-white to-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left Content */}
            <div className="scroll-animate opacity-0">
              <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-6">
                Our <span className="font-semibold">Story</span>
              </h2>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                ByteBios was founded with a simple mission: to bridge the gap between business needs and technological excellence. What started as a small team of passionate developers has grown into a trusted partner for enterprises across India.
              </p>
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                We believe that great software is not just about writing code—it's about understanding your business, anticipating your challenges, and delivering solutions that drive real growth and innovation.
              </p>
              <p className="text-lg text-gray-700 mb-8 leading-relaxed">
                Today, we're proud to have delivered 150+ successful projects for clients ranging from startups to Fortune 500 companies across fintech, banking, insurance, and technology sectors.
              </p>
              <Button
                asChild
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white transition-all duration-300 hover:scale-105 shadow-lg shadow-blue-500/30"
              >
                <Link to="/services">
                  Explore Our Work
                  <ArrowRight className="ml-2" size={20} />
                </Link>
              </Button>
            </div>

            {/* Right Image */}
            <div className="scroll-animate opacity-0">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl blur-2xl opacity-30"></div>
                <div className="relative bg-gradient-to-br from-blue-600 to-cyan-600 rounded-2xl p-8 md:p-12 overflow-hidden">
                  {/* Animated background elements */}
                  <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 right-0 w-40 h-40 bg-white rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 left-0 w-40 h-40 bg-white rounded-full blur-3xl"></div>
                  </div>
                  
                  <div className="relative h-96 flex flex-col items-center justify-center text-white">
                    {/* Main Stat */}
                    <div className="text-center mb-8">
                      <div className="text-7xl md:text-8xl font-bold mb-4 drop-shadow-lg">8+</div>
                      <div className="text-2xl md:text-3xl font-semibold mb-2 drop-shadow-md">Years of Excellence</div>
                    </div>

                    {/* Divider */}
                    <div className="w-20 h-1 bg-white/50 rounded-full mb-8"></div>

                    {/* Secondary Info */}
                    <div className="text-center space-y-4">
                      <p className="text-lg md:text-xl font-medium drop-shadow-md">Delivering innovation since 2016</p>
                      
                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-6 mt-8 pt-8 border-t border-white/30">
                        <div>
                          <div className="text-3xl md:text-4xl font-bold drop-shadow-md">150+</div>
                          <p className="text-sm md:text-base mt-2 drop-shadow-sm">Projects Delivered</p>
                        </div>
                        <div>
                          <div className="text-3xl md:text-4xl font-bold drop-shadow-md">50+</div>
                          <p className="text-sm md:text-base mt-2 drop-shadow-sm">Happy Clients</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate opacity-0">
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">
              Our <span className="font-semibold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">Core Values</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These principles guide everything we do
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const IconComponent = { Lightbulb, Target, Users, Award }[value.icon];
              return (
                <Card
                  key={index}
                  className="border-0 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 scroll-animate opacity-0 bg-gradient-to-br from-white to-blue-50/30"
                  style={{ transitionDelay: `${index * 100}ms` }}
                >
                  <CardContent className="p-8">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center mb-6">
                      {IconComponent && <IconComponent className="text-white" size={24} />}
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-24 bg-white relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16 scroll-animate opacity-0">
            <h2 className="text-4xl md:text-5xl font-light text-gray-900 mb-4">
              Why Choose <span className="font-semibold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent">ByteBios</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
            {[
              {
                title: 'Proven Track Record',
                description: '150+ successful projects delivered with 98% client satisfaction rate'
              },
              {
                title: 'Expert Team',
                description: 'Highly skilled professionals with deep expertise in latest technologies'
              },
              {
                title: 'Client-Centric Approach',
                description: 'Your success is our success. We partner with you every step of the way'
              },
              {
                title: 'Cutting-Edge Technology',
                description: 'Stay ahead with solutions built on the latest and most reliable tech stack'
              },
              {
                title: 'Scalable Solutions',
                description: 'From startups to enterprises, we build solutions that grow with you'
              },
              {
                title: 'Ongoing Support',
                description: '24/7 support and maintenance to keep your systems running smoothly'
              }
            ].map((item, index) => (
              <div
                key={index}
                className="scroll-animate opacity-0"
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="flex items-start space-x-4">
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md mt-1">
                    <span className="text-white font-bold">✓</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl"></div>

        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-light mb-6 scroll-animate opacity-0">
            Ready to Work with <span className="font-semibold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Us?</span>
          </h2>
          <p className="text-lg text-gray-300 mb-12 leading-relaxed scroll-animate opacity-0">
            Let's discuss how we can help transform your business with innovative technology solutions
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 scroll-animate opacity-0">
            <Button
              asChild
              size="lg"
              className="bg-white hover:bg-gray-100 text-gray-900 px-8 py-6 text-base transition-all duration-300 hover:scale-105 shadow-xl"
            >
              <Link to="/contact">
                Get In Touch
                <ArrowRight className="ml-2" size={20} />
              </Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-base transition-all duration-300 backdrop-blur-sm"
            >
              <Link to="/services">View Services</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
